// LayoutToggle — Classic ⇄ Modern 切替トグル (両レイアウト共通 chrome、置き場所 A).
//
// 背景 (docs/_wip/modern-layout.md §9.4.1 ① / ②、consult 2026-06-08):
//   - **① 置き場所 = A**: 右上の設定/オーバーフロー内に「表示: Classic / Modern」。
//     両 UI で同位置 = 迷わない + 描画 chrome を汚さない。ツール帯/ヘッダ常時可視
//     ボタン (B) は gateway 方針に逆行で不採用。Classic 側にも同じトグル項目を出す。
//   - **② 初回オンボーディング**: グローバル既定 Modern に着地した新規/旧ユーザへ、
//     切替トグルから **非ブロッキング** な coachmark「クラシック表示にも切り替え
//     られます」を **初回のみ** (localStorage seen フラグ)。モーダルで止めない。
//   - **④ スマホ = Modern 固定**: スマホでは切替トグル自体を非表示。これは呼び出し側
//     (ModernLayout/ClassicLayout) が `showToggle` で gate するので、この component は
//     「出すと決まったら出す」だけに専念する (= 表示判断は持たない)。
//
// このスライスの最小実装: 小さな inline トグル (Classic / Modern の 2 ボタン) +
// 初回 coachmark の吹き出し。ドロップダウンメニュー化や設定モーダル統合は後続。

import { useId, useState } from 'react';

import { createLayoutOnboardingSeenStore } from '../../infrastructure/layoutOnboardingSeen.js';
import type { LayoutKind } from '../../infrastructure/layoutPreference.js';

export interface LayoutToggleProps {
  /** 現在のレイアウト. */
  readonly current: LayoutKind;
  /** 切替. caller (useLayoutPreference.setLayout) が localStorage に永続化する. */
  readonly onChange: (next: LayoutKind) => void;
  /**
   * 初回 coachmark を出すか (= グローバル既定 Modern 着地時)。caller が seen フラグ
   * 未設定 かつ current === 'modern' のとき true を渡す。テスト容易性のため判定は
   * caller 側に置き、ここは「出すと言われたら出す」に専念。省略時 false。
   */
  readonly showOnboardingCoachmark?: boolean;
  /**
   * coachmark を表示し終えた (= seen を記録すべき) ときに呼ぶ。store 注入を避け、
   * 永続化経路は caller (ModernLayout) が持つ単一管轄に集約する。
   */
  readonly onCoachmarkSeen?: () => void;
}

/**
 * 「表示: [Classic] [Modern]」のセグメント風トグル + 初回 coachmark.
 *
 * coachmark は非ブロッキング (= 背景クリックを奪わない absolute 吹き出し)。
 * 「OK」または別レイアウト選択で閉じ、seen を記録する。
 */
export const LayoutToggle = ({
  current,
  onChange,
  showOnboardingCoachmark = false,
  onCoachmarkSeen,
}: LayoutToggleProps): React.ReactNode => {
  const groupId = useId();
  // coachmark の表示は初期 prop で 1 回だけ決める (= 再 render で勝手に再表示しない)。
  const [coachmarkOpen, setCoachmarkOpen] = useState<boolean>(showOnboardingCoachmark);

  const dismissCoachmark = (): void => {
    if (!coachmarkOpen) return;
    setCoachmarkOpen(false);
    onCoachmarkSeen?.();
  };

  const selectLayout = (next: LayoutKind): void => {
    // 別レイアウトを選んだ時点で coachmark の役目は終わり (= 存在を学んだ)。
    dismissCoachmark();
    if (next !== current) onChange(next);
  };

  return (
    <div className="relative flex items-center gap-1" data-testid="layout-toggle">
      <span id={groupId} className="select-none text-[11px] text-[var(--color-text-muted)]">
        表示:
      </span>
      {/* 2 ボタン = セグメント。aria-pressed で選択状態を伝える (= ラジオより軽い、
          両 UI で同じ見た目)。a11y 的にグループ化には fieldset を使う (= biome
          useSemanticElements に従う、role="group" の div を避ける)。 */}
      <fieldset aria-labelledby={groupId} className="m-0 flex border-0 p-0">
        {(['classic', 'modern'] as const).map((kind) => {
          const active = current === kind;
          const label = kind === 'classic' ? 'Classic' : 'Modern';
          return (
            <button
              key={kind}
              type="button"
              aria-pressed={active}
              onClick={() => selectLayout(kind)}
              data-manual="layout.classic-modern"
              className={`border px-2 py-0.5 text-[11px] font-bold ${
                active
                  ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-black'
                  : 'border-[var(--color-border)] bg-[var(--color-bg-soft)] text-white hover:bg-[var(--color-bg)]'
              }`}
            >
              {label}
            </button>
          );
        })}
      </fieldset>

      {/* 初回 coachmark (非ブロッキング吹き出し)。§9.4.1 ②: モーダルで止めない。
          absolute なので背景の描画/操作は奪わない。 */}
      {coachmarkOpen && (
        <div
          role="status"
          data-testid="layout-onboarding-coachmark"
          className="absolute top-full right-0 z-50 mt-1 w-56 rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-2 text-[11px] text-white shadow-lg"
        >
          <p className="mb-1 leading-snug">
            クラシック表示にも切り替えられます。旧バージョンに慣れた方はこちらへ。
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={dismissCoachmark}
              className="border border-[var(--color-border)] bg-[var(--color-bg-soft)] px-2 py-0.5 text-[11px] font-bold text-white hover:bg-[var(--color-bg)]"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * coachmark を出すべきかの判定 (= 純関数、test 容易)。
 * §9.4.1 ②: グローバル既定 Modern に着地 (= current === 'modern') かつ未 seen のとき。
 * Classic に既に居る (= 明示選択 or 過去に学んだ) ユーザには出さない。
 */
export const shouldShowOnboardingCoachmark = (current: LayoutKind, seen: boolean): boolean =>
  current === 'modern' && !seen;

/** seen store を生成する薄い re-export (caller が単一管轄で永続化するため). */
export { createLayoutOnboardingSeenStore };
